import { TenantSolution } from '../api/account/accountApiTypes';
import { TenantConfig } from '../tenants/config';
import { describeUseCase } from '../utils/useCases';

// Re-export for callers that don't need the broader Tenant types.
export type TrialSolution = TenantSolution;
export type TenantService = TenantConfig['services'][number];

const VALID: ReadonlySet<string> = new Set(['room', 'station', 'key', 'sup']);

// Solutions whose self-service signup is actually wired up end-to-end
// today. The full VALID set above tracks every solution the data model
// knows about; this narrower set is what the signup entry gate checks
// when a user arrives at /signup?solution=… from the marketing site.
// Add the next solution here (alongside its marketing page) when it
// ships — keep this list aligned with the buttons on kolula.com.
const SUPPORTED_SIGNUP: ReadonlySet<string> = new Set(['room']);

// Source of truth for "what services does each trial solution include".
// Mirrors docs/plans/self-service-onboarding.md §"Solutions table".
//
// Backend persists `tenant.solution` but does not yet derive
// `tenant.config.services` server-side — see the linked issue for the
// follow-up that lets us drop this map. Until then this is the canonical
// frontend mapping used both at signup time (for the synthetic config) and
// when a returning self-service user re-mounts the app. Keep it aligned with
// the backend copy in new-backend/app/src/lib/tenantConfig.ts.
//
// kolula ROOM conceptually covers venues *and* guest apartments, but new
// onboarded (self-service) room tenants start venue-only — only the venue
// use case is wired end-to-end today. Apartment is added back here (and in
// the backend map) once its self-service flow ships.
export const SOLUTION_TO_SERVICES: Record<TrialSolution, TenantService[]> = {
    room: ['venue'],
    station: ['rental', 'storage', 'parcel', 'p2p'],
    key: ['keys'],
    sup: ['sup'],
};

export const getServicesForSolution = (solution: TrialSolution): TenantService[] =>
    SOLUTION_TO_SERVICES[solution] ?? SOLUTION_TO_SERVICES.room;

/**
 * May a tenant switch this use case ON in Account → Use cases (phase 2 of
 * docs/plans/service-configuration-area.md; PO, 2026-08-18)? The use case's
 * `enableable` property (`utils/useCases.ts`). Mirror of `ENABLEABLE_SERVICES`
 * in `new-backend/admin/src/lib/tenantConfig.ts`, which rejects anything else.
 * `sup` is offered as a specific form of product rental; `keys` follows in a
 * fast successive step; `p2p` and `parcel` are set aside for now.
 */
export const isEnableableService = (service: string): boolean =>
    describeUseCase(service)?.enableable === true;

/**
 * The stored services a tenant keeps without being able to switch them
 * (pre-gate seeding, e.g. p2p/parcel on a station tenant). Account → Use cases
 * shows them locked-on, because omitting them from a save would strip a section
 * the tenant relies on.
 */
export const keptOnlyServices = (stored: string[]): string[] =>
    stored.filter((service) => !isEnableableService(service));

export const isTrialSolution = (value: unknown): value is TrialSolution =>
    typeof value === 'string' && VALID.has(value);

export const isSupportedSignupSolution = (
    value: unknown,
): value is TrialSolution => isTrialSolution(value) && SUPPORTED_SIGNUP.has(value);

// --- URL parsing (entry point: kolula.com/{solution} → /signup?solution=…) ---

export const parseSolutionFromUrl = (): TrialSolution | null => {
    const raw = new URLSearchParams(globalThis.location.search).get('solution');
    if (!raw) return null;
    const normalized = raw.trim().toLowerCase();
    return isTrialSolution(normalized) ? normalized : null;
};

// Returns the normalized URL param even when it's not a recognised
// solution, so the unsupported-solution screen can show the user what
// they actually requested (e.g. ?solution=sup → "kolula SUP isn't
// available yet"). Returns null if the param is absent. Output is
// length-capped to keep adversarial values from breaking the layout.
export const getRawSolutionFromUrl = (): string | null => {
    const raw = new URLSearchParams(globalThis.location.search).get('solution');
    if (!raw) return null;
    const normalized = raw.trim().toLowerCase();
    return normalized ? normalized.slice(0, 24) : null;
};

// --- Per-signup stash (sessionStorage; survives the Auth0 round trip) ---

const SIGNUP_STASH_KEY = 'kolula:signupSolution';

export const stashSignupSolution = (solution: TrialSolution): void => {
    globalThis.sessionStorage.setItem(SIGNUP_STASH_KEY, solution);
};

export const getStashedSignupSolution = (): TrialSolution | null => {
    const raw = globalThis.sessionStorage.getItem(SIGNUP_STASH_KEY);
    return isTrialSolution(raw) ? raw : null;
};

export const clearStashedSignupSolution = (): void => {
    globalThis.sessionStorage.removeItem(SIGNUP_STASH_KEY);
};

// --- Per-tenant cache (localStorage; survives session for returning users) ---

const STORED_SOLUTION_KEY = 'kolula:selectedSolution';

export const setStoredSolution = (solution: TrialSolution): void => {
    globalThis.localStorage.setItem(STORED_SOLUTION_KEY, solution);
};

export const getStoredSolution = (): TrialSolution | null => {
    const raw = globalThis.localStorage.getItem(STORED_SOLUTION_KEY);
    return isTrialSolution(raw) ? raw : null;
};

export const clearStoredSolution = (): void => {
    globalThis.localStorage.removeItem(STORED_SOLUTION_KEY);
};

// --- Per-tenant services cache (localStorage; survives session) ---
//
// The tenant's actual `config.services` (source of truth for what use cases it
// offers), cached from getAccount in TenantBootstrap so App.tsx can synthesize
// the self-service config before getAccount re-resolves. Falls back to the
// solution→services mapping when not yet cached (see getServicesForSolution).

const STORED_SERVICES_KEY = 'kolula:selectedServices';

export const setStoredServices = (services: TenantService[]): void => {
    if (services.length > 0) {
        globalThis.localStorage.setItem(STORED_SERVICES_KEY, JSON.stringify(services));
    }
};

export const getStoredServices = (): TenantService[] | null => {
    const raw = globalThis.localStorage.getItem(STORED_SERVICES_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0
            ? (parsed as TenantService[])
            : null;
    } catch {
        return null;
    }
};

export const clearStoredServices = (): void => {
    globalThis.localStorage.removeItem(STORED_SERVICES_KEY);
};

// --- Per-tenant organization name (localStorage; survives session) ---
//
// Cached so the brand area can render the tenant's name immediately on
// app boot, before getAccount returns. Gets refreshed on every getAccount
// success and cleared explicitly on logout (handled in the auth flow).

const STORED_ORG_NAME_KEY = 'kolula:organizationName';

export const setStoredOrganizationName = (name: string): void => {
    if (name) globalThis.localStorage.setItem(STORED_ORG_NAME_KEY, name);
};

export const getStoredOrganizationName = (): string | null =>
    globalThis.localStorage.getItem(STORED_ORG_NAME_KEY) || null;

export const clearStoredOrganizationName = (): void => {
    globalThis.localStorage.removeItem(STORED_ORG_NAME_KEY);
};

// Wipe everything we cache about the current self-service tenant. Call
// this on logout so the next user signing in via the same browser
// doesn't briefly see the previous tenant's brand text or services
// before their own getAccount completes.
export const clearSelfServiceCache = (): void => {
    clearStashedSignupSolution();
    clearStoredSolution();
    clearStoredServices();
    clearStoredOrganizationName();
};
